import { Observable, of } from 'rxjs'
import { IColumn, IValidationError } from './abstract-validator.interface'
import {
  AADHAR_NUMBER_PATTERN,
  BANK_ACCOUNT_NUMBER_PATTERN,
  ESI_NUMBER_PATTERN,
  IFSC_CODE_PATTERN,
  PAN_NUMBER_PATTERN,
  PF_NUMBER_PATTERN,
  PINCODE_PATTERN,
  UAN_NUMBER_PATTERN
} from 'src/app/validators/validators.validator'
import { convertToISO } from 'src/app/services/common/common.service'

export type DataValidatorFn = (col: IColumn) => Observable<IValidationError>
export type TemplateDownloadFn = () => void

export function DataValidatorEmail(): DataValidatorFn {
  return (col: IColumn): Observable<IValidationError> => {
    if (!col.value) {
      return of({ check: 'ValidEmail', status: 1, message: 'valid mail' })
    }
    if (/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(col.value)) {
      return of({ check: 'ValidEmail', status: 1, message: 'valid mail' })
    } else {
      return of({ check: 'ValidEmail', status: 0, message: 'Not a valid email id' })
    }
  }
}

export function DataValidatorRequired(): DataValidatorFn {
  return (col: IColumn): Observable<IValidationError> => {
    if (col.value === '' || col.value === null || col.value === undefined) {
      return of({ check: 'ValueRequired', status: 0, message: `${col.column} can not be blank` })
    } else {
      return of({ check: 'ValueRequired', status: 1, message: 'pass' })
    }
  }
}

export function DataValidatorForGender(): DataValidatorFn {
  return (col: IColumn): Observable<IValidationError> => {
    if (!col.value) {
      return of({ check: 'ValidGender', status: 1, message: 'Valid' })
    }
    const genders = ['FEMALE', 'MALE', 'OTHERS']
    if (genders.includes(col.value)) {
      return of({ check: 'ValidGender', status: 1, message: 'Valid' })
    } else {
      return of({
        check: 'ValidGender',
        status: 0,
        message: `Invalid. It should be one of ${genders.join(', ')} and must be in uppercase.`
      })
    }
  }
}

export function DataValidatorForMaritalStatus(): DataValidatorFn {
  return (col: IColumn): Observable<IValidationError> => {
    if (!col.value) {
      return of({ check: 'ValidMaritalStatus', status: 1, message: 'Valid' })
    }
    const validMaritialStatus = ['MARRIED', 'SINGLE', 'DIVORCED', 'SEPERATED', 'WIDOWED', 'OTHERS']
    if (validMaritialStatus.includes(col.value.toUpperCase())) {
      return of({ check: 'ValidMaritalStatus', status: 1, message: 'Valid' })
    } else {
      return of({
        check: 'ValidMaritalStatus',
        status: 0,
        message: `Invalid ${col.column}. It should one of ${validMaritialStatus.join(',')}`
      })
    }
  }
}

function patternValidator(check: string, pattern: RegExp, addtionalMsg: string = ''): DataValidatorFn {
  return (col: IColumn): Observable<IValidationError> => {
    if (!col.value) {
      return of({ check, status: 1, message: `valid ${col.column}` })
    }
    if (pattern.test(col.value)) {
      return of({ check, status: 1, message: `valid ${col.column}` })
    } else {
      return of({ check, status: 0, message: `Not a valid ${col.column} ${addtionalMsg}` })
    }
  }
}

export function DataValidatorMobileNumber(): DataValidatorFn {
  return patternValidator('ValidMobileNumber', /\+91-\d{10}$/, 'Accepted format +91-XXXXXXXXXX')
}

export function DataValidatorAadharNumber(): DataValidatorFn {
  return patternValidator('ValidAadhar', AADHAR_NUMBER_PATTERN)
}

export function DataValidatorPanNumber(): DataValidatorFn {
  return patternValidator('ValidPanNumber', PAN_NUMBER_PATTERN)
}

export function DataValidatorESINumber(): DataValidatorFn {
  return patternValidator('ValidESINumber', ESI_NUMBER_PATTERN)
}

export function DataValidatorUANNumber(): DataValidatorFn {
  return patternValidator('ValidUANNumber', UAN_NUMBER_PATTERN)
}

export function DataValidatorPFNumber(): DataValidatorFn {
  return patternValidator('ValidPFNumber', PF_NUMBER_PATTERN)
}

export function DataValidatorPincode(): DataValidatorFn {
  return patternValidator('ValidPincode', PINCODE_PATTERN)
}

export function DataValidatorBankAccountNumber(): DataValidatorFn {
  return patternValidator('ValidBankAccountNumber', BANK_ACCOUNT_NUMBER_PATTERN)
}

export function DataValidatorIFSCcode(): DataValidatorFn {
  return patternValidator('ValidIFSCcode', IFSC_CODE_PATTERN)
}

export function DataValidatorForEmployementType(): DataValidatorFn {
  return (col: IColumn): Observable<IValidationError> => {
    if (!col.value) {
      return of({ check: 'ValidEmploymentType', status: 1, message: 'Valid' })
    }
    const validEmploymentTypes = ['INTERN', 'REGULAR', 'IW', 'OTHERS']
    if (validEmploymentTypes.includes(col.value.toUpperCase())) {
      return of({ check: 'ValidEmploymentType', status: 1, message: 'Valid' })
    } else {
      return of({
        check: 'ValidEmploymentType',
        status: 0,
        message: `Invalid ${col.column}. It should one of ${validEmploymentTypes.join(',')}`
      })
    }
  }
}

export function DataValidatorForNomineeRelationship(): DataValidatorFn {
  return (col: IColumn): Observable<IValidationError> => {
    if (!col.value) {
      return of({ check: 'ValidNomineeRelationship', status: 1, message: 'Valid' })
    }
    const validNomineeRelationships = ['PARENT', 'SPOUSE', 'CHILDREN', 'SIBLING']
    if (validNomineeRelationships.includes(col.value.toUpperCase())) {
      return of({ check: 'ValidNomineeRelationship', status: 1, message: 'Valid' })
    } else {
      return of({
        check: 'ValidNomineeRelationship',
        status: 0,
        message: `Invalid. It should one of ${validNomineeRelationships.join(',')}`
      })
    }
  }
}

export function DataValidatorForNationality(): DataValidatorFn {
  return (col: IColumn): Observable<IValidationError> => {
    if (!col.value) {
      return of({ check: 'ValidNationality', status: 1, message: 'Valid' })
    }
    const validNationalities = ['INDIAN', 'OTHERS']
    if (validNationalities.includes(col.value.toUpperCase())) {
      return of({ check: 'ValidNationality', status: 1, message: 'Valid' })
    } else {
      return of({
        check: 'ValidNationality',
        status: 0,
        message: `Invalid. It should one of ${validNationalities.join(',')}`
      })
    }
  }
}

export function DateValidatorForFutureDate(): DataValidatorFn {
  return (col: IColumn): Observable<IValidationError> => {
    if (!col.value) {
      return of({ check: 'ValidDate', status: 1, message: 'Valid' })
    }

    const inputDate = new Date(convertToISO(col.value))

    if (isNaN(inputDate.getTime())) {
      return of({
        check: 'ValidDate',
        status: 0,
        message: `Invalid ${col.column}. Please provide a valid date.`
      })
    }

    const currentDate = new Date()

    if (inputDate > currentDate) {
      return of({
        check: 'ValidDate',
        status: 0,
        message: `Invalid ${col.column}. Date cannot be in the future.`
      })
    }

    return of({ check: 'ValidDate', status: 1, message: 'Valid' })
  }
}

export const validateUAN = event => {
  const inputElement = event.target as HTMLInputElement
  const inputValue = inputElement.value
  const sanitizedValue = inputValue.replace(/[^0-9]/g, '')
  inputElement.value = sanitizedValue
}

export const validatePFNumber = event => {
  const inputElement = event.target as HTMLInputElement
  inputElement.value = inputElement.value.toUpperCase()
}

export function DataValidatorForEmployeeStatus(): DataValidatorFn {
  return (col: IColumn): Observable<IValidationError> => {
    if (!col.value) {
      return of({ check: 'ValidEmployeeStatus', status: 1, message: 'Valid' })
    }
    if (['ACTIVE', 'TERMINATED'].includes(col.value.toUpperCase())) {
      return of({ check: 'ValidEmployeeStatus', status: 1, message: 'Valid' })
    } else {
      return of({ check: 'ValidEmployeeStatus', status: 0, message: `Invalid, Accepted values are ACTIVE, TERMINATED` })
    }
  }
}

export function DataValidatorForNotificationPreferences(): DataValidatorFn {
  return (col: IColumn): Observable<IValidationError> => {
    if (!col.value) {
      return of({ check: 'ValidNotificationPreferences', status: 1, message: 'Valid' })
    }

    const validOptions = ['EMAIL', 'SMS']

    const values = col.value.split(',').filter(v => v.length > 0)

    const isValid = values.every(v => validOptions.includes(v))

    if (isValid) {
      return of({ check: 'ValidNotificationPreferences', status: 1, message: 'Valid' })
    } else {
      return of({
        check: 'ValidNotificationPreferences',
        status: 0,
        message: `Invalid, Accepted values are ${validOptions.join(', ')}`
      })
    }
  }
}

export function DataValidatorForDDMMYYYY(): DataValidatorFn {
  return (col: IColumn): Observable<IValidationError> => {
    if (!col.value || col.value.trim().length === 0) {
      return of({ check: 'ValidDateFormat', status: 1, message: 'Valid' })
    }

    const value = col.value.trim()

    const regex = /^([0-2][0-9]|3[0-1])\/(0[1-9]|1[0-2])\/\d{4}$/

    if (!regex.test(value)) {
      return of({
        check: 'ValidDateFormat',
        status: 0,
        message: `${col.column} Invalid (Expected DD/MM/YYYY)`
      })
    }

    const [day, month, year] = value.split('/').map(Number)
    const dateObj = new Date(year, month - 1, day)

    const isRealDate = dateObj.getFullYear() === year && dateObj.getMonth() === month - 1 && dateObj.getDate() === day

    if (!isRealDate) {
      return of({
        check: 'ValidDateFormat',
        status: 0,
        message: `Invalid Date, Accepted Format is DD/MM/YYYY`
      })
    }

    return of({ check: 'ValidDateFormat', status: 1, message: 'Valid' })
  }
}

export function DataValidatorForTwoDecimalNumber(): DataValidatorFn {
  return (col: IColumn): Observable<IValidationError> => {
    if (!col.value || col.value.trim().length === 0) {
      return of({ check: 'ValidTwoDecimalNumber', status: 1, message: 'Valid' })
    }

    const value = col.value.trim()

    // Whole: 1+ digits, Decimal: exactly 2 digits (mandatory)
    const regex = /^\d+\.\d{2}$/

    if (!regex.test(value)) {
      return of({
        check: 'ValidTwoDecimalNumber',
        status: 0,
        message: `Invalid (Expected format: NNNN.NN)`
      })
    }

    return of({ check: 'ValidTwoDecimalNumber', status: 1, message: 'Valid' })
  }
}

export function DataValidatorForYesNo(): DataValidatorFn {
  return (col: IColumn): Observable<IValidationError> => {
    if (!col.value || col.value.trim().length === 0) {
      return of({ check: 'ValidYesNo', status: 1, message: 'Valid' })
    }

    const value = col.value.trim().toUpperCase()

    const validValues = ['YES', 'NO']

    if (validValues.includes(value)) {
      return of({ check: 'ValidYesNo', status: 1, message: 'Valid' })
    }

    return of({
      check: 'ValidYesNo',
      status: 0,
      message: `Invalid (Expected: YES or NO)`
    })
  }
}

export function DataValidatorForYesNoCaseInsensitive(): DataValidatorFn {
  return (col: IColumn): Observable<IValidationError> => {
    if (!col.value || col.value.trim().length === 0) {
      return of({ check: 'ValidYesNo', status: 1, message: 'Valid' })
    }

    const value = col.value.trim().toUpperCase()

    const validValues = ['YES', 'NO']

    if (validValues.includes(value)) {
      return of({ check: 'ValidYesNo', status: 1, message: 'Valid' })
    }

    return of({
      check: 'ValidYesNo',
      status: 0,
      message: `Invalid (Expected: Yes, No, YES, NO, yes, no)`
    })
  }
}

export function DataValidatorForFirstLetterCaps(): DataValidatorFn {
  return (col: IColumn): Observable<IValidationError> => {
    if (!col.value || col.value.trim().length === 0) {
      return of({ check: 'ValidNameFormat', status: 1, message: 'Valid' })
    }

    const value = col.value.trim()

    /*
      Regex Rules:
      - One or more words
      - Word can be:
          A
          Anjali
          Lakshmi
      - Space separated
    */
    const regex = /^([A-Z][a-z]*)(\s[A-Z][a-z]*)*$/

    if (!regex.test(value)) {
      return of({
        check: 'ValidNameFormat',
        status: 0,
        message: `Invalid, (Expected: Each word starts with CAPS followed by small letters)`
      })
    }

    return of({ check: 'ValidNameFormat', status: 1, message: 'Valid' })
  }
}

export function DataValidatorForPassportNumber(): DataValidatorFn {
  return (col: IColumn): Observable<IValidationError> => {
    if (!col.value || col.value.trim().length === 0) {
      return of({ check: 'ValidPassportNumber', status: 1, message: 'Valid' })
    }

    const value = col.value.trim()

    const regex = /^[A-Za-z0-9]{5,20}$/

    if (!regex.test(value)) {
      return of({
        check: 'ValidPassportNumber',
        status: 0,
        message: `Please enter a valid Passport Number`
      })
    }

    return of({ check: 'ValidPassportNumber', status: 1, message: 'Valid' })
  }
}

export function DataValidatorForAlphabetsOnly(): DataValidatorFn {
  return (col: IColumn): Observable<IValidationError> => {
    if (!col.value || col.value.trim().length === 0) {
      return of({ check: 'ValidAlphabetsOnly', status: 1, message: 'Valid' })
    }

    const value = col.value.trim()

    const regex = /^[A-Za-z]+$/

    if (!regex.test(value)) {
      return of({
        check: 'ValidAlphabetsOnly',
        status: 0,
        message: `${col.key} Invalid (Only alphabets allowed)`
      })
    }

    return of({ check: 'ValidAlphabetsOnly', status: 1, message: 'Valid' })
  }
}
